import type { ReactNode } from 'react';
import { Download, FileSpreadsheet, FileText, FileType } from 'lucide-react';

const FILE_EXT_RE = /\.(pdf|xlsx|xls|docx|doc|csv)(\?|#|$)/i;
const DOWNLOAD_TEXT_RE = /t[eé]l[eé]charg/i;

function isDownloadLink(text: string, url: string): boolean {
  return FILE_EXT_RE.test(url) || DOWNLOAD_TEXT_RE.test(text);
}

/**
 * M9: only allow safe URL schemes for rendered links. Chat content is
 * model-generated and can echo product/customer names, so a crafted
 * `javascript:` / `data:` link is a stored-XSS vector (which could exfiltrate
 * the auth token). Browsers ignore control chars / whitespace inside a scheme
 * (e.g. "java\tscript:" parses as "javascript:"), so strip those before
 * checking. Returns the href to use, or null when the scheme is unsafe (the
 * caller then renders plain text instead of a link).
 */
function safeHref(url: string): string | null {
  // Strip C0 control chars (\x00-\x1F), DEL (\x7F) and all whitespace before
  // detecting the scheme — browsers ignore them when parsing the URL.
  // eslint-disable-next-line no-control-regex
  const cleaned = url.replace(/[\x00-\x1F\x7F\s]/g, '');
  const scheme = cleaned.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
  if (!scheme) {
    // No scheme → relative URL or anchor, safe to render.
    return url.trim();
  }
  const allowed = ['http', 'https', 'mailto', 'tel'];
  return allowed.includes((scheme[1] ?? '').toLowerCase()) ? url.trim() : null;
}

function fileIcon(url: string): ReactNode {
  if (/\.pdf/i.test(url)) return <FileText className="size-3.5 text-red-500" />;
  if (/\.(xlsx|xls|csv)/i.test(url))
    return <FileSpreadsheet className="size-3.5 text-emerald-500" />;
  if (/\.(docx|doc)/i.test(url)) return <FileType className="size-3.5 text-blue-500" />;
  return <Download className="size-3.5" />;
}

function formatInline(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const patterns = [
      { type: 'bold' as const, match: remaining.match(/\*\*(.+?)\*\*/) },
      { type: 'inlineCode' as const, match: remaining.match(/`([^`]+)`/) },
      { type: 'link' as const, match: remaining.match(/\[(.+?)\]\((.+?)\)/) },
    ];

    const candidates = patterns
      .filter((pattern) => pattern.match)
      .map((pattern) => ({
        ...pattern,
        index: remaining.indexOf((pattern.match as RegExpMatchArray)[0]),
      }))
      .sort((a, b) => a.index - b.index);

    if (candidates.length === 0) {
      parts.push(remaining);
      break;
    }

    const candidate = candidates[0];
    if (!candidate) {
      parts.push(remaining);
      break;
    }
    const match = candidate.match as RegExpMatchArray;

    if (candidate.index > 0) {
      parts.push(remaining.slice(0, candidate.index));
    }

    if (candidate.type === 'bold') {
      parts.push(<strong key={key++}>{match[1]}</strong>);
    } else if (candidate.type === 'inlineCode') {
      parts.push(<code key={key++}>{match[1]}</code>);
    } else {
      // candidate.type === 'link'
      const linkText = match[1] ?? '';
      const href = safeHref(match[2] ?? '');
      if (href === null) {
        // M9: unsafe scheme (javascript:/data:/vbscript:…) — never render a link.
        parts.push(<span key={key++}>{linkText}</span>);
      } else if (isDownloadLink(linkText, href)) {
        parts.push(
          <a
            key={key++}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-sm font-medium text-charcoal-700 no-underline shadow-soft-sm transition-colors hover:bg-neutral-100 hover:text-charcoal-900"
          >
            {fileIcon(href)}
            {linkText}
            <Download className="size-3 text-charcoal-400" />
          </a>,
        );
      } else {
        parts.push(
          <a key={key++} href={href} target="_blank" rel="noopener noreferrer">
            {linkText}
          </a>,
        );
      }
    }

    remaining = remaining.slice(candidate.index + match[0].length);
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

export function renderChatMarkdown(content: string): ReactNode[] {
  const blocks = content.replace(/\r\n/g, '\n').split('\n\n');

  return blocks.map((block, index) => {
    const trimmed = block.trim();

    if (!trimmed) {
      return null;
    }

    if (trimmed.startsWith('### ')) {
      return <h3 key={index}>{formatInline(trimmed.slice(4))}</h3>;
    }

    if (trimmed.startsWith('## ')) {
      return <h2 key={index}>{formatInline(trimmed.slice(3))}</h2>;
    }

    if (trimmed.startsWith('```') && trimmed.endsWith('```')) {
      const code = trimmed.replace(/^```[a-zA-Z0-9_-]*\n?/, '').replace(/\n?```$/, '');
      return (
        <pre key={index}>
          <code>{code}</code>
        </pre>
      );
    }

    if (/^\d+\.\s+/.test(trimmed)) {
      const items = trimmed
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => /^\d+\.\s+/.test(line))
        .map((line) => line.replace(/^\d+\.\s+/, ''));
      return (
        <ol key={index}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>{formatInline(item)}</li>
          ))}
        </ol>
      );
    }

    if (trimmed.startsWith('- ')) {
      const items = trimmed
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.startsWith('- '))
        .map((line) => line.slice(2));
      return (
        <ul key={index}>
          {items.map((item, itemIndex) => (
            <li key={itemIndex}>{formatInline(item)}</li>
          ))}
        </ul>
      );
    }

    const lines = trimmed.split('\n');

    return (
      <p key={index}>
        {lines.map((line, lineIndex) => (
          <span key={lineIndex}>
            {formatInline(line)}
            {lineIndex < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
    );
  });
}
